import type { Role } from './types';

// ---------------------------------------------------------------------------
// Roles & statuses
// ---------------------------------------------------------------------------

export const ROLES: readonly Role[] = ['owner', 'manager', 'staff'] as const;

export const ORDER_STATUSES = [
  'awaiting_payment',
  'placed',
  'accepted',
  'ready',
  'completed',
  'cancelled',
  'refunded',
] as const;

export const RESERVATION_STATUSES = [
  'requested',
  'confirmed',
  'declined',
  'cancelled',
  'no_show',
  'seated',
] as const;

export const PRINT_JOB_STATUSES = ['queued', 'claimed', 'printed', 'failed'] as const;

/** Status changes a dashboard member may request. The API/DB validates transitions. */
export const ORDER_STATUS_TRANSITIONS: Record<string, readonly string[]> = {
  awaiting_payment: ['placed', 'cancelled'],
  placed: ['accepted', 'ready', 'completed', 'cancelled'],
  accepted: ['ready', 'completed', 'cancelled'],
  ready: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
  refunded: [],
};

export const RESERVATION_STATUS_TRANSITIONS: Record<string, readonly string[]> = {
  requested: ['confirmed', 'declined', 'cancelled'],
  confirmed: ['seated', 'no_show', 'cancelled'],
  seated: ['completed', 'no_show'],
  declined: [],
  cancelled: [],
  no_show: [],
};

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

export const LIMITS = {
  /** Maximum quantity of a single line. */
  maxLineQuantity: 99,
  /** Hard ceiling on total item quantity per order (location override exists). */
  defaultMaxItemsPerOrder: 30,
  maxNotesLength: 500,
  maxNameLength: 120,
  maxPhoneLength: 40,
  maxEmailLength: 254,
  /** 5 MB upload cap. */
  maxUploadBytes: 5 * 1024 * 1024,
  /** Customer order/reservation links live this long. */
  customerTokenTtlDays: 90,
  /** Pairing codes expire after 10 minutes. */
  pairingCodeTtlSeconds: 10 * 60,
  /** Pairing attempts allowed per IP per minute before blocking. */
  pairingAttemptsPerMinute: 5,
  /** Staff invites expire after 7 days. */
  inviteTtlDays: 7,
  /** Claimed print jobs are re-claimable after this long (crash recovery). */
  printJobClaimSeconds: 60,
  /** Card checkout sessions expire after 30 minutes. */
  checkoutSessionMinutes: 30,
  /** Printer offline alert threshold (dashboard banner). */
  printerOfflineMinutes: 2,
  /** Email the owner only after this long offline (PLAN.md §16). */
  printerOfflineAlertMinutes: 10,
  /** PII retention default, configurable per platform. */
  piiRetentionMonths: 12,
} as const;

export const ALLOWED_UPLOAD_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
] as const;

/** Magic-byte signatures for the image types we accept. SVG is deliberately absent. */
export const IMAGE_SIGNATURES: ReadonlyArray<{ mime: string; bytes: number[]; offset?: number }> = [
  { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
  { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { mime: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
  // RIFF....WEBP
  { mime: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46] },
];

// ---------------------------------------------------------------------------
// Slugs
// ---------------------------------------------------------------------------

/** Subdomains that must never resolve to a restaurant. */
export const RESERVED_SLUGS: readonly string[] = [
  'www',
  'app',
  'api',
  'admin',
  'dashboard',
  'cdn',
  'static',
  'mail',
  'email',
  'help',
  'support',
  'status',
  'docs',
  'blog',
  'stores',
  'proxy',
  'adop',
  'stripe',
  'login',
  'auth',
  'billing',
  'account',
  'store',
  'media',
  'assets',
];

/**
 * Minimal profanity/squatting guard for public web addresses. Deliberately short:
 * the hosted Supabase project can extend it, and the reserved list is the real gate.
 */
export const BLOCKED_SLUG_WORDS: readonly string[] = [
  'fuck',
  'shit',
  'cunt',
  'nigger',
  'nigga',
  'faggot',
  'rape',
  'porn',
  'nazi',
  'hitler',
  'sex',
  'xxx',
  'scam',
  'phishing',
];

// ---------------------------------------------------------------------------
// Branding
// ---------------------------------------------------------------------------

export const FONTS = [
  { id: 'inter', label: 'Inter (modern)', stack: "'Inter', ui-sans-serif, system-ui, sans-serif" },
  {
    id: 'dm-sans',
    label: 'DM Sans (friendly)',
    stack: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
  },
  {
    id: 'space-grotesk',
    label: 'Space Grotesk (bold)',
    stack: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
  },
  {
    id: 'playfair',
    label: 'Playfair Display (elegant)',
    stack: "'Playfair Display', Georgia, serif",
  },
  {
    id: 'merriweather',
    label: 'Merriweather (classic)',
    stack: "'Merriweather', Georgia, serif",
  },
] as const;

export const DEFAULT_PRIMARY_COLOR = '#111827';
export const DEFAULT_ACCENT_COLOR = '#f59e0b';

/** Hex color, 3 or 6 digits. */
export const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// ---------------------------------------------------------------------------
// Printer
// ---------------------------------------------------------------------------

export const PAPER_WIDTHS = [58, 80] as const;
export type PaperWidth = (typeof PAPER_WIDTHS)[number];

/** Printable character columns for the common monospace thermal fonts. */
export const PAPER_COLUMNS: Record<PaperWidth, number> = {
  58: 32,
  80: 48,
} as const;

export const PRINTER_POLL_INTERVAL_MS = 4000;
export const PRINTER_HEARTBEAT_INTERVAL_MS = 60_000;
/** Max receipts returned from one /jobs poll. */
export const PRINTER_JOBS_BATCH = 5;

// ---------------------------------------------------------------------------
// Storefront / marketplace
// ---------------------------------------------------------------------------

export const MARKETPLACE_PAGE_SIZE = 24;
export const STOREFRONT_CACHE_SECONDS = 60;

/** Custom-domain lifecycle values stored in `domains.status`. */
export const DOMAIN_STATUSES = ['pending_dns', 'pending_ssl', 'active', 'failed', 'removed'] as const;

/** Days a retired slug keeps redirecting before it can be claimed again. */
export const SLUG_REDIRECT_DAYS = 30;

// ---------------------------------------------------------------------------
// Demo / seed data (used by seed.sql copies and E2E fixtures)
// ---------------------------------------------------------------------------

export const DEMO_RESTAURANT = {
  slug: 'joespizza-downtown',
  name: "Joe's Pizza — Downtown",
  timezone: 'America/New_York',
} as const;
